import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";
import { useToast } from "../../../context/toastContext";
import { useConfirm } from "../../../context/confirmContext";
import { downloadFromApi } from "../../../utils/downloadBlob";

const LICENSE_TITLES = ["owner", "manager", "employee", "buyer", "budtender"];

const UserManagement = ({ userProfile }) => {
    const toast = useToast();
    const confirm = useConfirm();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false); 
    const [search, setSearch] = useState("");
    const [expandedUsers, setExpandedUsers] = useState(new Set());
    const tableRef = useRef(null);
    const [colWidths, setColWidths] = useState([4, 22, 22, 12, 9, 15, 11]);

    const [selected, setSelected] = useState(new Set());
    const [bulkLicenseNo, setBulkLicenseNo] = useState("");
    const [bulkTitle, setBulkTitle] = useState("employee");
    const [bulkSubmitting, setBulkSubmitting] = useState(false);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ platformRole: "user" });
            if (search) params.set("search", search);
            const res = await apiClient.get(`/admin/users?${params.toString()}`);
            setUsers(res.data);
        } catch (err) {
            console.error(err);
        } finally { 
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleSearch = (e) => {
        if (e.key === "Enter") fetchUsers();
    };

    const toggleExpand = (userId) => {
        setExpandedUsers((prev) => {
            const next = new Set(prev);
            next.has(userId) ? next.delete(userId) : next.add(userId);
            return next;
        });
    };

    const handleToggleStatus = async (user) => {
        const action = user.enabled ? "deactivate" : "activate";
        const label = action === "activate" ? "Enable" : "Disable";
        const ok = await confirm({
            title: `${label} User`,
            message: `${label} ${user.firstName} ${user.lastName}?`,
            confirmLabel: label,
            variant: action === "deactivate" ? "danger" : "primary",
        });
        if (!ok) return;
        try {
            await apiClient.put(`/admin/users/${user.userId}/status`, { action });
            setUsers((prev) => prev.map((u) => u.userId === user.userId ? { ...u, enabled: !u.enabled } : u));
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update status");
        }
    };

    const startResize = (index) => (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidths = [...colWidths];
        const tableWidth = tableRef.current?.offsetWidth || 1000;
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
        const onMove = (moveE) => {
            const delta = ((moveE.clientX - startX) / tableWidth) * 100;
            const next = [...startWidths];
            next[index] = Math.max(4, startWidths[index] + delta);
            next[index + 1] = Math.max(4, startWidths[index + 1] - delta);
            setColWidths(next);
        };
        const onUp = () => {
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    const formatPhone = (phone) => {
        if (!phone) return "—";
        if (phone.startsWith("+1") && phone.length === 12)
            return `+1 (${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
        return phone;
    };

    const toggleSelected = (userId) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(userId) ? next.delete(userId) : next.add(userId);
            return next;
        });
    };

    const handleBulkAttach = async () => {
        if (!bulkLicenseNo.trim() || selected.size === 0) return;
        setBulkSubmitting(true);
        try {
            const res = await apiClient.post("/admin/users/licenses/bulk", {
                operation: "attach",
                userIds: Array.from(selected),
                licenseNo: bulkLicenseNo.trim(),
                title: bulkTitle,
            });
            const failed = res.data.results.filter((r) => r.status === "skipped" || r.status === "failed");
            if (failed.length) {
                toast.error(`${failed.length} user(s) could not be attached (already on that license or invalid).`);
            } else {
                toast.success(`Attached ${res.data.results.length} user(s) to ${bulkLicenseNo.trim()}.`);
            }
            setSelected(new Set());
            setBulkLicenseNo("");
        } catch (err) {
            toast.error(err.response?.data?.error || "Bulk attach failed");
        } finally {
            setBulkSubmitting(false);
        }
    };

    const handleResendInvite = async (user) => {
        try {
            const res = await apiClient.post(`/admin/users/${user.userId}/resend-invite`);
            toast.success(res.data.emailSent ? "Invite resent." : "Temp password reset, but the email failed to send.");
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to resend invite");
        }
    };

    const handleExportCsv = async () => {
        try {
            const params = new URLSearchParams({ platformRole: "user" });
            if (search) params.set("search", search);
            await downloadFromApi(`/admin/users/export.csv?${params.toString()}`, `dyc-users-${new Date().toISOString().slice(0, 10)}.csv`);
        } catch (err) {
            toast.error("Failed to export users");
        }
    };

    const HEADERS = ["", "Name", "Email", "Phone", "Status", "Licenses", "Action"];

    return (
        <div>
            <div className="admin-search-bar">
                <input
                    className="admin-search-input"
                    placeholder="Search name, email, phone, company, license…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearch}
                />
                <span className="admin-search-count">{users.length} user{users.length !== 1 ? "s" : ""}</span>
                <button className="admin-action-btn" onClick={handleExportCsv}>Export CSV</button>
            </div>

            {selected.size > 0 && (
                <div className="admin-bulk-toolbar">
                    <span>{selected.size} selected</span>
                    <input
                        className="admin-form-input"
                        placeholder="License #"
                        value={bulkLicenseNo}
                        onChange={(e) => setBulkLicenseNo(e.target.value)}
                    />
                    <select className="admin-form-input" value={bulkTitle} onChange={(e) => setBulkTitle(e.target.value)}>
                        {LICENSE_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button
                        className="admin-action-btn primary"
                        disabled={!bulkLicenseNo.trim() || bulkSubmitting}
                        onClick={handleBulkAttach}
                    >
                        {bulkSubmitting ? "Attaching…" : "Attach to License"}
                    </button>
                    <button className="admin-action-btn" onClick={() => setSelected(new Set())}>Clear</button>
                </div>
            )}

            {loading ? (
                <div className="admin-loading-wrapper"><Loading /></div>
            ) : (
                <div className="admin-table-scroll" ref={tableRef}>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                {HEADERS.map((label, i) => (
                                    <th key={label} style={{ width: `${colWidths[i]}%`, position: "relative" }}>
                                        {label}
                                        {i < HEADERS.length - 1 && (
                                            <div className="col-resize-handle" onMouseDown={startResize(i)} />
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 && (
                                <tr><td colSpan={HEADERS.length} className="admin-table-empty">No users found.</td></tr>
                            )}
                            {users.map((user) => {
                                const isDisabled = !user.enabled;
                                const isExpanded = expandedUsers.has(user.userId);
                                const hasLicenses = user.licenses?.length > 0;
                                return (
                                    <React.Fragment key={user.userId}>
                                        {/* ── User row ── */}
                                        <tr className={isDisabled ? "admin-row-disabled" : ""}>
                                            <td className="admin-td-secondary">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(user.userId)}
                                                    onChange={() => toggleSelected(user.userId)}
                                                />
                                            </td>
                                            <td className="admin-td-name">
                                                {hasLicenses && (
                                                    <span className="admin-collapse-toggle" onClick={() => toggleExpand(user.userId)}>
                                                        {isExpanded ? "▼" : "▶"}
                                                    </span>
                                                )}
                                                {user.firstName} {user.lastName}
                                                {isDisabled && <span className="admin-disabled-badge">Disabled</span>}
                                            </td>
                                            <td className="admin-td-secondary">{user.email}</td>
                                            <td className="admin-td-secondary">{formatPhone(user.phoneNumber)}</td>
                                            <td className={isDisabled ? "admin-status-disabled" : "admin-status-active"}>
                                                {isDisabled ? "Disabled" : "Active"}
                                            </td>
                                            <td className="admin-td-secondary">
                                                {hasLicenses ? (
                                                    <span className="admin-license-expand-trigger" onClick={() => toggleExpand(user.userId)}>
                                                        {user.licenses.length} license{user.licenses.length !== 1 ? "s" : ""}
                                                        <span className="admin-expand-chevron">{isExpanded ? " ▲" : " ▼"}</span>
                                                    </span>
                                                ) : (
                                                    <span className="admin-table-none">No licenses</span>
                                                )}
                                            </td>
                                            <td className="admin-td-action">
                                                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                                                    <button
                                                        className={`admin-action-btn ${isDisabled ? "enable" : "disable"}`}
                                                        onClick={() => handleToggleStatus(user)}
                                                    >
                                                        {isDisabled ? "Enable" : "Disable"}
                                                    </button>
                                                    {user.userStatus === "FORCE_CHANGE_PASSWORD" && (
                                                        <button className="admin-action-btn" onClick={() => handleResendInvite(user)}>
                                                            Resend Invite
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        {/* ── License detail panel ── */}
                                        {isExpanded && hasLicenses && (
                                            <tr className="admin-detail-row">
                                                <td colSpan={HEADERS.length} className="admin-detail-cell">
                                                    <div className="admin-license-detail-panel">
                                                        {user.licenses.map((l) => (
                                                            <div key={l.licenseNo} className="admin-license-detail-card">
                                                                <div className="admin-license-detail-company">{l.company}</div>
                                                                <div className="admin-license-detail-row">
                                                                    <span className="admin-license-detail-no">{l.licenseNo}</span>
                                                                    <span className="admin-role-tag">{l.userRole}</span>
                                                                </div>
                                                                {(l.industry || l.profile) && (
                                                                    <div className="admin-license-detail-meta">
                                                                        {[l.industry, l.profile].filter(Boolean).join(" · ")}
                                                                    </div>
                                                                )}
                                                                {l.city && (
                                                                    <div className="admin-license-detail-meta">{l.city}, {l.state}</div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
